import { FC, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import logoND from '../../assets/next-devs-com-frase.jpg'
import logoINI from '../../assets/logo_ini.png'
import logoSYTI from '../../assets/logo_syti.svg'
import { 
  Award, 
  ChevronRight, 
  Clock, 
  ExternalLink, 
  Heart, 
  Star, 
  Users, 
  X, 
  Zap,
  Search,
  Code,
  Briefcase,
  Rocket,
  ArrowRight,
  Link
} from 'lucide-react';

interface ProjectMetrics {
  [key: string]: string;
}

interface Project {
  title: string;
  description: string;
  image: string;
  tags: string[];
  category: string;
  metrics: ProjectMetrics;
  url?: string;
}

const getMetricIcon = (key: string) => {
  const icons = {
    usuarios: Users,
    avaliação: Star,
    conclusão: Award,
    estudantes: Users,
    horas: Clock,
    satisfação: Heart,
    clientes: Users,
    uptime: Zap,
    conversão: ChevronRight
  };
  return icons[key.toLowerCase() as keyof typeof icons] || ChevronRight;
};

const ProjectCard: FC<{ 
  project: Project; 
  onClick: () => void; 
  index: number;
}> = ({ 
  project, 
  onClick, 
  index
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={onClick}
      className="group cursor-pointer"
    >
      <div className="relative bg-black/50 rounded-xl overflow-hidden border border-blue-900/30 hover:border-blue-500/30 transition-all duration-300 backdrop-blur-sm">
        <div className="aspect-video relative overflow-hidden">
          <motion.img 
            src={project.image}
            alt={project.title}
            className={`w-full h-full transition-all duration-500 ${
              project.image.endsWith('.svg') 
                ? 'object-contain p-6 bg-blue-900/50' 
                : 'object-cover'
            }`}
            whileHover={{ scale: 1.05 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
            {project.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs rounded-full bg-blue-900/80 text-blue-200 backdrop-blur-sm"
              >
                {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-400">
                +{project.tags.length - 3}
              </span>
            )}
          </div>
        </div>
        
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200 group-hover:from-blue-400 group-hover:to-blue-600 transition-all duration-300">
              {project.title}
            </h3>
          </div>
          
          <p className="text-blue-200 text-sm mb-4 line-clamp-2 group-hover:text-blue-100 transition-colors duration-300">
            {project.description}
          </p>
          
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(project.metrics).slice(0, 3).map(([key, value], i) => {
              const Icon = getMetricIcon(key);
              return (
                <div
                  key={i}
                  className="text-center p-2 rounded-lg bg-blue-900/50 group/metric"
                >
                  <Icon className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                  <div className="text-blue-400 font-bold text-sm">
                    {value}
                  </div>
                  <div className="text-xs text-blue-300">
                    {key}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ProjectModal: FC<{
  project: Project | null;
  onClose: () => void;
}> = ({ project, onClose }) => {
  if (!project) return null;

  const category = categories.find(cat => cat.id === project.category);
  const CategoryIcon = category?.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          onClick={e => e.stopPropagation()}
          className="bg-black rounded-xl w-full max-w-lg relative overflow-hidden"
        >
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1.5 rounded-lg bg-blue-900/80 backdrop-blur-sm z-10 hover:bg-blue-800/80 transition-colors"
          >
            <X className="w-4 h-4 text-blue-200" />
          </button>

          <div className="h-48 relative">
            <img
              src={project.image}
              alt={project.title}
              className={`w-full h-full ${
                project.image.endsWith('.svg')
                  ? 'object-contain p-6 bg-blue-900/50'
                  : 'object-cover'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          </div>

          <div className="p-4 -mt-8 relative">
            <div className="flex items-center gap-2 mb-3">
              {CategoryIcon && (
                <div className="p-1.5 rounded-lg bg-blue-500/20">
                  <CategoryIcon className="w-3.5 h-3.5 text-blue-400" />
                </div>
              )}
              <h3 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                {project.title}
              </h3>
            </div>

            <p className="text-blue-200 text-xs leading-relaxed mb-4">
              {project.description}
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {Object.entries(project.metrics).map(([key, value], i) => {
                const Icon = getMetricIcon(key);
                return (
                  <div
                    key={i}
                    className="text-center p-2 rounded-lg bg-blue-900/80 group"
                  >
                    <Icon className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                    <div className="text-blue-400 font-bold text-sm">
                      {value}
                    </div>
                    <div className="text-[10px] text-blue-300">
                      {key}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Code className="w-3.5 h-3.5 text-blue-400" />
                <h4 className="text-xs font-medium text-blue-200">
                  Tecnologias
                </h4>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {project.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-0.5 text-xs rounded-md bg-blue-900/80 text-blue-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {project.url && (
              <motion.a
                href={project.url}
                
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 text-white text-xs font-medium group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link className="w-3.5 h-3.5" />
                <span>Visitar Projeto</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const categories = [
  { id: 'all', label: 'Todos', icon: Briefcase },
  { id: 'education', label: 'Educação', icon: Code },
  { id: 'business', label: 'Negócios', icon: Rocket }
];

const Projects: FC = () => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const allProjects = [
    {
      title: 'Plataforma de Ensino Gratuita',
      description: 'Plataforma inovadora de ensino de programação com cursos interativos e sistema de mentoria. Focada em proporcionar uma experiência prática e envolvente para desenvolvedores iniciantes.',
      image: logoND,
      tags: ['React', 'TypeScript', 'TailWindCSS', 'FireBase', 'FireStore'],
      category: 'education',
      metrics: {
        Usuários: '200+',
        Avaliação: '4.8/5',
        Conclusão: '92%'
      },
      url: 'https://nextdevsacademy.vercel.app/'
    },
    {
      title: 'Aulas ao Vivo de Programação',
      description: 'Projeto de ensino focado em aulas de programação ao vivo.',
      image: logoINI,
      tags: ['Python', 'HTML', 'CSS', 'JavaScript', 'React', 'Inteligência artificial', 'C#', 'Linkedin', 'Git e GitHub'],
      category: 'education',
      metrics: {
        Estudantes: '600+',
        Horas: '300+',
        Satisfação: '96%'
      },
      url: 'https://iniciantilp.vercel.app/'
    },
    {
      title: 'Desenvolvimento de Sites',
      description: 'Plataforma de criação de sites profissionais para empresas.',
      image: logoSYTI,
      tags: ['Landing Page', 'SEO', 'Responsividade', 'UI/UX', 'Experiência do Usuário', 'Suporte Técnico'],
      category: 'business',
      metrics: {
        Clientes: '10+',
        Uptime: '99.9%',
        Conversão: '+45%'
      },
      url: 'https://sytiup.vercel.app/'
    }
  ];

  const filteredProjects = allProjects
    .filter(project => activeTab === 'all' || project.category === activeTab)
    .filter(project => 
      searchTerm === '' || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  return (
    <section id="projects" className="relative py-20">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-blue-900/20 to-black" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <motion.span 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 text-sm mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <Rocket className="w-5 h-5" />
            <span>Portfólio</span>
          </motion.span>
          
          <h2 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600 mb-6">
            Projetos em Destaque
          </h2>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Explore alguns dos principais projetos que desenvolvi, cada um com seu próprio conjunto de desafios e soluções únicas.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-8">
            <motion.div 
              className="flex gap-2 p-1 bg-blue-900/50 rounded-lg backdrop-blur-sm"
              whileHover={{ scale: 1.02 }}
            >
              {categories.map(category => (
                <motion.button
                  key={category.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab(category.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-300 ${
                    activeTab === category.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white'
                      : 'text-blue-300 hover:text-white hover:bg-blue-800/50'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.label}</span>
                </motion.button>
              ))}
            </motion.div>

            <motion.div 
              className="relative w-full md:w-auto"
              whileHover={{ scale: 1.02 }}
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar projetos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 pl-9 pr-4 py-2 text-sm rounded-lg bg-blue-900/50 text-blue-200 border border-blue-800/50 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all duration-300 backdrop-blur-sm"
              />
            </motion.div>
          </div>

          <motion.div 
            layout
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode="wait">
              {filteredProjects.map((project, index) => (
                <ProjectCard
                  key={project.title}
                  project={project as unknown as Project}
                  onClick={() => setSelectedProject(project as unknown as Project)}
                  index={index}
                />
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredProjects.length === 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center py-12"
            >
              <div className="inline-block p-3 bg-blue-500/10 rounded-full mb-3">
                <Search className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg text-blue-200 mb-2">Nenhum projeto encontrado</h3>
              <p className="text-blue-300 text-sm">
                Tente buscar por outros termos ou tecnologias
              </p>
            </motion.div>
          )}
        </div>
      </div>

      <ProjectModal 
        project={selectedProject} 
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
};

export default Projects;